import React, { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { Card } from './ui/card';

interface PrinterSelectionProps {
  onPrinterSelect: (printerId: string, printerName: string) => void;
  selectedPrinter?: string;
}

interface PrinterOption {
  id: string;
  name: string;
  model: string;
  specifications: {
    printSpeed: string;
    resolution: string;
    colors: string[];
  };
}

export const PrinterSelection: React.FC<PrinterSelectionProps> = ({ 
  onPrinterSelect, 
  selectedPrinter = 'hp_1' 
}) => {
  const [selected, setSelected] = useState('hp_1'); // Always default to HP

  // Auto-select HP printer and notify parent component
  useEffect(() => {
    const hpPrinter = printerOptions.find(p => p.id === 'hp_1');
    if (hpPrinter) {
      setSelected('hp_1');
      onPrinterSelect('hp_1', hpPrinter.name);
    }
  }, [onPrinterSelect]);

  const printerOptions: PrinterOption[] = [
    {
      id: 'hp_1',
      name: 'HP LaserJet Pro M404n',
      model: 'HP_404',
      specifications: {
        printSpeed: '38 ppm',
        resolution: '600 x 600 dpi',
        colors: ['Monochrome']
      }
    }
    // Temporarily disabled other printers
    // {
    //   id: 'samsung_1',
    //   name: 'Samsung Xpress M2026W',
    //   model: 'Samsung_M2026W',
    //   specifications: {
    //     printSpeed: '26 ppm',
    //     resolution: '600 x 600 dpi',
    //     colors: ['Monochrome']
    //   }
    // },
    // {
    //   id: 'samsung_2',
    //   name: 'Samsung Xpress C460W',
    //   model: 'Samsung_C460W',
    //   specifications: {
    //     printSpeed: '25 ppm',
    //     resolution: '600 x 600 dpi',
    //     colors: ['Color', 'Monochrome']
    //   }
    // }
  ];

  const handleSelect = (printerId: string, printerName: string) => {
    setSelected(printerId);
    onPrinterSelect(printerId, printerName);
  };

  return (
    <div style={{
      animation: 'float 6s ease-in-out infinite, fadeIn 0.5s ease',
      animationDelay: '0.2s',
      width: '100%'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '2rem',
        boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1), 0 10px 20px -5px rgba(0,0,0,0.04)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '0.75rem', 
          marginBottom: '2rem' 
        }}>
          <div style={{
            width: '48px', height: '48px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)'
          }}>
            <Printer style={{ width: '24px', height: '24px', color: '#16a34a' }} />
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Selected Printer</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {printerOptions.map((printer) => (
            <div
              key={printer.id}
              style={{
                padding: '1.25rem',
                border: '2px solid #22c55e',
                borderRadius: '16px',
                background: 'linear-gradient(to right, #f0fdf4, #ffffff)',
                boxShadow: '0 4px 15px -3px rgba(34, 197, 94, 0.15)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(34, 197, 94, 0.2)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px -3px rgba(34, 197, 94, 0.15)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                  {/* Printer Name */}
                  <h4 style={{ fontWeight: '800', color: '#0f172a', fontSize: '16px', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {printer.name}
                    <span style={{ fontSize: '10px', background: '#22c55e', color: 'white', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Active</span>
                  </h4>
                  <div style={{ fontSize: '13px', color: '#475569', display: 'flex', gap: '1.25rem', flexWrap: 'wrap', fontWeight: '500' }}>
                    <span><strong style={{ color: '#94a3b8', marginRight: '4px' }}>Speed:</strong>{printer.specifications.printSpeed}</span>
                    <span><strong style={{ color: '#94a3b8', marginRight: '4px' }}>Resolution:</strong>{printer.specifications.resolution}</span>
                  </div>
                </div>

                {/* Check Mark */}
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)'
                }}>
                  <svg style={{ width: '16px', height: '16px', color: 'white' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div style={{ 
          marginTop: '1.5rem', 
          padding: '0.75rem', 
          background: '#f8fafc', 
          borderRadius: '12px', 
          textAlign: 'center',
          border: '1px solid #f1f5f9' 
        }}>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0, fontWeight: '500' }}>
            More printers coming soon
          </p>
        </div>
      </div>
      
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        `}
      </style>
    </div>
  );
};
